package app.nestbridge.feature.careteam.presentation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import app.nestbridge.domain.identity.usecase.GetCurrentUserUseCase
import app.nestbridge.domain.identity.model.ProfessionalServiceType
import app.nestbridge.feature.careteam.model.CareTeam
import app.nestbridge.feature.careteam.model.CareTeamMember
import app.nestbridge.feature.careteam.model.TeamRelationshipRole
import app.nestbridge.feature.careteam.usecase.SaveCareTeamUseCase
import app.nestbridge.feature.careteam.screen.CreateCareTeamEvent
import app.nestbridge.feature.careteam.screen.CreateCareTeamScreen
import app.nestbridge.feature.careteam.screen.CreateCareTeamState
import app.nestbridge.feature.careteam.screen.TeamCreationResult
import app.nestbridge.feature.children.model.Child
import app.nestbridge.feature.children.usecase.GetChildUseCase
import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import kotlinx.coroutines.launch
import app.nestbridge.app.util.launchSafely
import me.tatarka.inject.annotations.Assisted
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import kotlin.time.Clock.System

@CircuitInject(
    screen = CreateCareTeamScreen::class,
    scope = AppScope::class,
)
@Inject
class CreateCareTeamPresenter(
    @Assisted private val screen: CreateCareTeamScreen,
    @Assisted private val navigator: Navigator,
    private val saveCareTeamUseCase: SaveCareTeamUseCase,
    private val getChildUseCase: GetChildUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
) : Presenter<CreateCareTeamState> {

    @Composable
    override fun present(): CreateCareTeamState {
        val scope = rememberCoroutineScope()

        var child by remember { mutableStateOf<Child?>(null) }
        var isLoading by remember { mutableStateOf(true) }
        var careTeamType: ProfessionalServiceType? by remember { mutableStateOf(null) }
        var description by remember { mutableStateOf("") }
        var isSaving by remember { mutableStateOf(false) }

        suspend fun loadChild() {
            isLoading = true
            child = if (screen.childId.isNotEmpty()) {
                getChildUseCase(screen.childId)
            } else {
                null
            }
            isLoading = false
        }

        fun onEvent(event: CreateCareTeamEvent) {
            when (event) {
                CreateCareTeamEvent.OnAppear -> {
                    kotlinx.coroutines.MainScope().launchSafely(onError = { isLoading = false }) {
                        loadChild()
                    }
                }

                CreateCareTeamEvent.Back -> navigator.pop(
                    TeamCreationResult(
                        status = TeamCreationResult.Status.Cancelled,
                    ),
                )
                CreateCareTeamEvent.Submit -> {
                    if (isSaving) return
                    val currentChild = child ?: return
                    val currentCareTeamType = careTeamType ?: return

                    isSaving = true

                    scope.launch {
                        runCatching {
                            val userId = getCurrentUserUseCase()?.id
                                ?: error("No current user")
                            val now = System.now()

                            // Create care team with self as organizer
                            val careTeam = CareTeam(
                                id = "",
                                childId = currentChild.id,
                                teamType = currentCareTeamType,
                                description = description.takeIf { it.isNotBlank() },
                                members = listOf(
                                    CareTeamMember(
                                        userId = userId,
                                        role = TeamRelationshipRole.ORGANIZER,
                                    ),
                                ),
                                status = "Active",
                                createdAt = now,
                                createdBy = userId,
                                modifiedAt = now,
                                modifiedBy = userId,
                            )

                            saveCareTeamUseCase(careTeam)
                        }.onSuccess {
                            navigator.pop(
                                TeamCreationResult(
                                    status = TeamCreationResult.Status.Completed,
                                ),
                            )
                        }.onFailure {
                            // Save failed. On session expiry the auth gate resets to
                            // login; otherwise just re-enable the button.
                            isSaving = false
                        }
                    }
                }
                is CreateCareTeamEvent.UpdateCareTeamType -> careTeamType = event.value
                is CreateCareTeamEvent.UpdateDescription -> description = event.value
            }
        }

        return CreateCareTeamState(
            child = child,
            careTeamType = careTeamType,
            description = description,
            isLoading = isLoading,
            isSaving = isSaving,
            eventSink = ::onEvent,
        )
    }
}
